import { DrawableType, UtilsService } from 'src/app/services/utils.service';
import { Component, OnInit, Input, EventEmitter, Output, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import { MatCheckboxChange } from '@angular/material/checkbox';

export interface DataTableColumn {
    name?: string;
    prop?: string;
    parseValue?: (value: any, column: DataTableColumn, index: number) => any;
    template?: TemplateRef<any>;
    templateParams?: any;
    type?: DrawableType;
    hidden?: () => boolean;
    cellClass?: string;
    width?: string;
    minWidth?: string;
    maxWidth?: string;
    sortable?: boolean;
}

export interface MenuButton<T> {
    label: string;
    action: (row: T) => void;
    isDisabled?: (row: T) => boolean;
}

export interface PaginatorStatus {
    page: number;
    itemsPerPage: number;
    totalElementCount?: number;
}

export interface SortConfiguration {
    dir?: Exclude<SortDirection, ''>;
    prop: string;
}

@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DataTableComponent<T> implements OnInit {

    @ViewChild('menu', {static: false})
    menu: any;

    @Input()
    set rows(rows: T[]) {
        this.originalRows = rows;
        if (this.isEnabledLocalPagination()) {
            this.paginatorStatus.totalElementCount = rows.length;
            this.updateRowsWithLocalPagination();
        }
    }
    get rows(): T[] {
        if (this.isEnabledLocalPagination()) {
            return this.paginatedRows;
        } else {
            return this.originalRows;
        }
    }

    @Input()
    columns: DataTableColumn[] = [];

    @Input()
    menuButtons: MenuButton<T>[] = [];

    @Input()
    paginatorStatus: PaginatorStatus;

    @Input()
    externalPagination = false;

    @Input()
    layoutMode: 'auto' | 'fixed' = 'auto';

    @Output()
    updateRows = new EventEmitter<PageEvent>();

    @Output()
    sortFn = new EventEmitter<SortConfiguration | undefined>();

    @Input()
    selectionType?: 'single' | 'multiple';

    selectedValue?: T[];
    @Input()
    get selected() {
        return this.selectedValue ?? [];
    }
    set selected(newValue: T[] | undefined) {
        this.selectedValue = newValue;
        this.selectedChange.emit(this.selected);
    }

    @Output()
    selectedChange = new EventEmitter<T[]>();

    @Input()
    stickyHeader?: boolean;

    @Input()
    stickyHeaderBackground?: string;

    @Input()
    rowId?: (row: T) => any;

    @Input() maxHeight = '';
    @Input() height = '';
    @Input() maxWidth = '';
    @Input() width = '';
    @Input() minWidth = '';
    @Input() overflow?: string;

    activeMenuRow?: any;

    paginatedRows: any[];

    originalRows: any[];

    constructor(
        private utilsService: UtilsService
    ) { }

    ngOnInit() {
        if (this.isEnabledLocalPagination()) {
            this.paginatorStatus.totalElementCount = this.originalRows?.length;
            this.updateRowsWithLocalPagination();
        }
    }

    callUpdateRows(event: PageEvent) {
        if (this.isEnabledLocalPagination()) {
            this.paginatorStatus.itemsPerPage = event.pageSize;
            this.paginatorStatus.page = event.pageIndex;
            this.updateRowsWithLocalPagination();
        } else {
            this.updateRows.emit(event);
        }
    }

    updateRowsWithLocalPagination() {
        this.paginatedRows = this.originalRows?.slice(this.paginatorStatus.itemsPerPage * this.paginatorStatus.page, this.paginatorStatus.itemsPerPage * (this.paginatorStatus.page + 1));
    }

    isEnabledLocalPagination(): boolean {
        return !!this.paginatorStatus && !this.externalPagination;
    }

    getCellContent(row: T, rowIndex: number, column: DataTableColumn) {
        const route = column.prop?.split('.').map(x => x.trim());
        let value: any = row;
        if (route) {
            for (const item of route) {
                if (value !== null && value !== undefined && typeof value === 'object') {
                    value = value[item];
                } else {
                    break;
                }
            }
        }
        let result = this.utilsService.parseByType(value, column.type);
        if (column.parseValue) {
            result = column.parseValue(result, column, rowIndex);
        }
        return result;
    }

    getColumnsToShow() {
        const res: string[] = [];
        // Columna de las checkboxes
        if (this.selectionType === 'multiple')
            res.push('checkboxes-column');
        // Columnas genéricas
        let i = 0;
        for (const column of this.columns) {
            if (!column.hidden || column.hidden()) {
                res.push(i.toString());
            }
            i++;
        }
        // Columna de los botones
        if (this.menuButtons?.length)
            res.push('buttons-column');
        return res;
    }

    onSortChange(sort: Sort) {
        const configuration: SortConfiguration | undefined = !sort.direction ? undefined :  {
            dir: sort.direction,
            prop: this.columns[Number.parseInt(sort.active, 10)].prop
        };
        this.sortFn.emit(configuration);
    }

    selectAllChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selected = Object.assign([], this.rows);
        } else {
            this.selected = [];
        }
    }

    compareRows(row1: T, row2: T) {
        return this.rowId ? this.rowId(row1) === this.rowId(row2) : row1 === row2;
    }

    isSelected(row: T) {
        return this.selected.some(current => this.compareRows(current, row));
    }

    allSelected() {
        return this.rows.every(row => this.isSelected(row));
    }

    updateSelection(row: T, event: MatCheckboxChange) {
        const selectedCopy = Object.assign([], this.selected);
        if (event.checked && !this.isSelected(row)) {
            selectedCopy.push(row);
        } else if (!event.checked) {
            const index = selectedCopy.findIndex(value => this.compareRows(value, row));
            if (index !== undefined) {
                selectedCopy.splice(index, 1);
            }
        }
        this.selected = selectedCopy;
    }

    handleRowClick(row: T, event: MouseEvent) {
        if (this.selectionType === 'single') {
            if (this.isSelected(row)) {
                this.selected = [];
            } else {
                this.selected = [row];
            }
        }
    }
}
